import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { Observable, BehaviorSubject } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { BaseResponse, BaseService } from 'src/app/core/services/base.service';
import { User } from 'src/app/shared/models/user';
import { LocalStorage } from 'src/app/core/localstorage';
import { UserService } from '../user/user.service';
import { Router } from '@angular/router';
import {
    OAuthResponse,
    PassRecoverUpdatePayload,
    PassRecoveryFindPayload,
    PassRecoveryGetCodePayload,
    PassRecoveryValidatePayload,
} from './auth.interface';
import { WINDOW } from '@core/http/window.providers';

export interface PreRegisterData {
    email: string;
    document: string;
    zip_code: string;
    celular_ddi: string;
    celular_ddd: string;
    celular_numero: string;
}

@Injectable()
export class AuthService extends BaseService {
    constructor(
        private http: HttpClient,
        private userService: UserService,
        private router: Router,
        @Inject(WINDOW) private window: Window
    ) {
        super();
    }

    /**
     * Realiza pre cadastro do usuário
     * @param user dados do usuário para cadastro
     */
    setRegister(user: PreRegisterData) {
        const body = this.setBody();
        Object.keys(user).forEach((key) => body.set(key, user[key]));

        const response = this.http
            .post(this.urlService + this.routeAdress.route_user_set_register, body.toString())
            .pipe(map(this.extractData), catchError(this.serviceError));

        return response;
    }

    /**
     * registerUser() - Registar novo usuário
     * @param user
     */
    registerUser(user) {
        const body = this.setBody();
        Object.keys(user).forEach((key) => body.set(key, user[key]));

        const response = this.http
            .post(this.urlService + this.routeAdress.route_user_add_new_register, body.toString())
            .pipe(map(this.extractData), catchError(this.serviceError));

        return response;
    }

    /**
     * Solicita token de autenticação
     * @param player id do usuário
     * @param type_sending tipo de envio (1 - Email | 2 - SMS)
     * @param type_auth tipo da requisição (0 - Recuperação de senha | 1 - Cadastro  | 2 - Cancelamento de inscrição)
     * @param extra dados extras
     */
    requestToken(player, type_sending, type_auth, extra?) {
        const body = this.setBody();
        body.set('player', player);
        body.set('type_sending', type_sending);
        body.set('type_auth', type_auth);

        if (extra) {
            Object.keys(extra).forEach((key) => body.set(key, extra[key]));
        }

        const route =
            type_auth === '2'
                ? this.routeAdress.route_tournament_send_cancel_confirmation_code
                : this.routeAdress.route_user_send_authentication_code;

        const response = this.http
            .post(this.urlService + route, body.toString())
            .pipe(map(this.extractData), catchError(this.serviceError));

        return response;
    }

    /**
     * Valida token de autenticação
     * @param player id do usuário
     * @param code token
     * @param code_token token secreto
     */
    validateToken(player, code, code_token) {
        const body = this.setBody();
        body.set('player', player);
        body.set('code', code);
        body.set('code_token', code_token);

        const response = this.http
            .post(this.urlService + this.routeAdress.route_user_receive_authentication_code, body.toString())
            .pipe(map(this.extractData), catchError(this.serviceError));

        return response;
    }

    /**
     * login() - Efetuar login no sistema
     * @param user
     */
    login(userForm) {
        let body = this.setBody();
        body.set('accessId', userForm.email);
        body.set('password', userForm.password);

        let response = this.http
            .post(this.urlService + this.routeAdress.route_auth_login, body.toString())
            .pipe(map(this.extractData), catchError(this.serviceError));
        return response;
    }

    logout() {
        let body = this.setBody();
        let response = this.http.post(this.urlService + this.routeAdress.route_auth_logoff, body.toString()).pipe(
            map(this.extractData),
            map((resp) => {
                this.localStorage.resetUser();
                this.localStorage.saveToken(resp.token);
                return resp;
            }),
            catchError(this.serviceError)
        );
        return response;
    }

    /**
     * Obtem metodos de recuperação de senha
     */
    getPassRecoveryMethods() {
        const body = this.setBody();

        const response = this.http
            .post(this.urlService + this.routeAdress.route_user_recovery_methods, body.toString())
            .pipe(map(this.extractData), catchError(this.serviceError));

        return response;
    }

    /**
     * Procura usuário para recuperação de senha
     */
    passRecoveryFind(params: PassRecoveryFindPayload) {
        const body = this.setBody();
        this.setParams(body, params);

        const response = this.http
            .post(this.urlService + this.routeAdress.route_user_recovery_found, body.toString())
            .pipe(map(this.extractData), catchError(this.serviceError));

        return response;
    }
    /**
     * Recebe código para troca de senha
     */
    passRecoveryGetCode(params: PassRecoveryGetCodePayload) {
        const body = this.setBody();
        this.setParams(body, params);

        const response = this.http
            .post(this.urlService + this.routeAdress.route_user_recovery_get_code, body.toString())
            .pipe(map(this.extractData), catchError(this.serviceError));

        return response;
    }

    /**
     * Valida código para troca de senha
     */
    passRecoveryValidateCode(params: PassRecoveryValidatePayload) {
        const { code, code_token } = params;
        const body = this.setBody();
        body.set('code', code);
        body.set('code_token', code_token);

        /**
         * @TODO: AJUSTAR URL BASE
         */
        const response = this.http
            .post(this.urlService + this.routeAdress.route_user_recovery_validate_code, body.toString())
            .pipe(map(this.extractData), catchError(this.serviceError));

        return response;
    }

    /**
     * Valida código para troca de senha
     */
    passRecoveryUpdatePass(params: PassRecoverUpdatePayload) {
        const { password, passwordConfirm } = params;
        const body = this.setBody();
        body.set('password', password);
        body.set('passwordConfirm', passwordConfirm);

        /**
         * @TODO: AJUSTAR URL BASE
         */
        const response = this.http
            .post(this.urlService + this.routeAdress.route_user_update_password, body.toString())
            .pipe(map(this.extractData), catchError(this.serviceError));

        return response;
    }

    requestPassword({ email }) {
        let body = this.setBody();
        body.set('mail', email);
        let response = this.http
            .post(this.urlService + this.routeAdress.route_auth_request_password, body.toString())
            .pipe(map(this.extractData), catchError(this.serviceError));
        return response;
    }
    //prettier-ignore
    changePassword({ currentPassword, newPassword, confirmNewPassword }, player) {
        let body = this.setBody();
        if (player) {
            body.set('player', player);
        }
        
        if (currentPassword) {
            body.set('actualPassword', currentPassword);
        }
        body.set('newPassword', newPassword);
        body.set('repeatPassword', confirmNewPassword);
        body.set('changeType', '2');

        let response = this.http
            .post(
                this.urlService + this.routeAdress.route_user_update_password,
                body.toString()
            )
            .pipe(map(this.extractData), catchError(this.serviceError));
        return response;
    }

    validateAccessCode(code) {
        const body = this.setBody();
        body.set('codeId', code);

        return this.http
            .post(this.urlService + this.routeAdress.route_auth_validate, body.toString())
            .pipe(map(this.extractData), catchError(this.serviceError));
    }

    oAuth(token) {
        const body = this.setBody();
        body.set('token', token);
        if (body.get('host') == 'null') {
            const localFallback = 'testecbt.tenisintegrado.com.br';
            const locationHostname = this.window.location.hostname;
            const host = locationHostname.includes('localhost') ? localFallback : locationHostname;
            body.set('host', host);
        }

        return this.http
            .post<BaseResponse<OAuthResponse>>(this.urlService + this.routeAdress.route_auth_get_me, body.toString())
            .pipe(catchError(this.serviceError));
    }

    isAuthenticated() {
        return !!this.userService.currentUser.getValue();
    }

    saveToken(token) {
        this.localStorage.saveToken(token);
    }
}
